import { useNavigate, useParams } from 'react-router-dom';
import { PageShell } from '../../layout/Shell';
import { listReplays } from '../../shared/api/client';
import { summaryFromApi } from '../../shared/api/transform';
import { Icon } from '../../shared/design/Icons';
import { Button, EmptyState, TierBadge } from '../../shared/design/Primitives';
import { useAsync } from '../../shared/hooks/useAsync';
import { formatTime, shortMapName } from '../../shared/model/Replay';

export function MapDetailPage() {
  const navigate = useNavigate();
  const { challengeUid: rawUid } = useParams<{ challengeUid: string }>();
  const challengeUid = rawUid ? decodeURIComponent(rawUid) : '';

  const { data, loading, error } = useAsync(
    () => listReplays(500, challengeUid),
    [challengeUid],
  );
  const rows = (data ?? []).map(summaryFromApi);
  const mapLabel = shortMapName(challengeUid);
  const fastestMs = rows.length ? Math.min(...rows.map((r) => r.raceTimeMS)) : 0;

  return (
    <PageShell active="leaderboard">
      <div className="tm" style={{ maxWidth: 1320, margin: '0 auto', padding: '28px 24px 80px' }}>
        <button
          onClick={() => navigate('/leaderboard')}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--fg-3)',
            fontSize: 12,
            fontFamily: 'var(--font-mono)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: 0,
            marginBottom: 12,
          }}
        >
          ← All maps
        </button>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
          <h1 className="h1" style={{ fontFamily: 'var(--font-mono)' }}>{mapLabel}</h1>
          <div style={{ color: 'var(--fg-3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
            {loading ? 'Loading…' : `${rows.length} replays`}
            {fastestMs > 0 && (
              <span style={{ marginLeft: 12 }}>
                Fastest <span style={{ color: 'var(--fg-1)', fontWeight: 600 }}>{formatTime(fastestMs)}</span>
              </span>
            )}
          </div>
        </div>
        <p className="p" style={{ marginBottom: 24, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-muted)' }}>
          ChallengeUID · {challengeUid}
        </p>

        {error ? (
          <EmptyState title="Couldn't load map" body={error.message} icon={<Icon.Warn />} accent="var(--bad)" />
        ) : loading ? (
          <div
            style={{
              height: 240,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-8)',
            }}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No replays for this map"
            body="The map record exists but its replays were removed. Scan or import some to repopulate."
            icon={<Icon.Scan />}
            cta={
              <Button variant="primary" icon={<Icon.Scan />} onClick={() => navigate('/')}>
                Scan a replay
              </Button> as any
            }
          />
        ) : (
          <div
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-8)',
              overflow: 'hidden',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-surface-2)' }}>
                  {['#', 'Player', 'Time', 'Δ', 'Top', 'Tier', 'Sus.', 'Scanned', ''].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        padding: '11px 14px',
                        textAlign: ['Time', 'Δ', 'Top', 'Sus.'].includes(h) ? 'right' : 'left',
                        color: 'var(--fg-3)',
                        fontWeight: 500,
                        fontSize: 11,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const rank = i + 1;
                  const deltaMs = row.raceTimeMS - fastestMs;
                  return (
                    <tr
                      key={row.ghostUid}
                      style={{
                        borderTop: '1px solid var(--border-subtle)',
                        background: i % 2 === 1 ? 'rgba(255,255,255,0.012)' : 'transparent',
                      }}
                    >
                      <td
                        style={{
                          padding: '12px 14px',
                          fontFamily: 'var(--font-mono)',
                          color: rank <= 3 ? 'var(--accent)' : 'var(--fg-3)',
                          fontWeight: 600,
                        }}
                      >
                        {rank <= 3 ? `0${rank}` : rank}
                      </td>
                      <td style={{ padding: '12px 14px', color: 'var(--fg-1)', fontWeight: 500 }}>{row.author}</td>
                      <td
                        style={{
                          padding: '12px 14px',
                          textAlign: 'right',
                          fontFamily: 'var(--font-mono)',
                          color: 'var(--fg-1)',
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {row.raceTimeStr}
                      </td>
                      <td
                        style={{
                          padding: '12px 14px',
                          textAlign: 'right',
                          fontFamily: 'var(--font-mono)',
                          color: rank === 1 ? 'var(--ok)' : 'var(--fg-3)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {rank === 1 ? '—' : `+${(deltaMs / 1000).toFixed(3)}s`}
                      </td>
                      <td
                        style={{
                          padding: '12px 14px',
                          textAlign: 'right',
                          fontFamily: 'var(--font-mono)',
                          color: 'var(--fg-2)',
                        }}
                      >
                        {row.topSpeedKmh} <span style={{ color: 'var(--fg-muted)', fontSize: 11 }}>km/h</span>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <TierBadge tier={row.tier} size="sm" label={false} />
                      </td>
                      <td
                        style={{
                          padding: '12px 14px',
                          textAlign: 'right',
                          fontFamily: 'var(--font-mono)',
                          color: row.clusterCount > 3 ? 'var(--bad)' : row.clusterCount > 0 ? 'var(--warn)' : 'var(--fg-muted)',
                        }}
                      >
                        {row.clusterCount === 0 ? '—' : `×${row.clusterCount}`}
                      </td>
                      <td
                        style={{
                          padding: '12px 14px',
                          color: 'var(--fg-3)',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 12,
                        }}
                      >
                        {row.scanDate ? row.scanDate.slice(0, 10) : ''}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        <button
                          onClick={() => navigate(`/scan/${encodeURIComponent(row.ghostUid)}`)}
                          style={{
                            background: 'transparent',
                            border: '1px solid var(--border)',
                            color: 'var(--fg-2)',
                            cursor: 'pointer',
                            padding: '4px 10px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontFamily: 'var(--font-sans)',
                            fontWeight: 500,
                          }}
                        >
                          Open →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
}
